import React, { ComponentProps, useCallback, useMemo, useState } from 'react';

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  UniqueIdentifier,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { ArrayFieldTemplateProps, RJSFSchema } from '@rjsf/utils';
import { JSONSchema7 } from 'json-schema';
import { useSnackbar } from 'notistack';

import AddIcon from '@mui/icons-material/Add';
import CopyAllIcon from '@mui/icons-material/CopyAll';
import DataArrayIcon from '@mui/icons-material/DataArray';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { camelCaseToSpaceSeparated } from '~/utils/stringUtils';

import { ActiveAccordionContext, ArrayFieldItemContext } from './ActiveAccordionContext';
import BreadcrumbsLegendTemplate from './BreadcrumbsLegendTemplate';
import { formIdToFieldDepthConverter } from './formUtils';
import { ExtendedRJSFormContext } from './useExtendedRJSForm';

interface Props extends ArrayFieldTemplateProps<unknown, RJSFSchema, ExtendedRJSFormContext> {
  uiSchema: {
    'ui:options': {
      isInitiallyCollapsed: boolean; // collapse the array initially, because they're all expanded by default
      canCopyFromSibling: boolean; // enable the ability to copy values from siblings
      canCopyArrayItems: boolean; // enable the ability to copy items within the array
      canCopyArrayItemsFromSimilar: string; // enable the ability to copy items within the array from similar items
      uncopyableProperties: Array<string>; // define fields that cannot be copied
      itemTitleProperty: string; // define the property key used to display an item as an option
      layout: 'grid' | 'stack'; // switch between vertical stack or grid layout
      toolbarDirection?: 'row' | 'column'; // direction of buttons in toolbar
      simpleLayout: boolean; // some sort of distinction between tour specific design and more general that we can use elsewhere
      displaySingleOption?: boolean; // only allow one accordion to be open at a time
    };
  };
}

export default function ExtendedArrayFieldTemplate({
  canAdd,
  className,
  disabled,
  formData,
  fieldPathId,
  items,
  readonly,
  registry,
  required,
  schema,
  title,
  uiSchema,
  onAddClick,
}: Props) {
  const formContext = registry.formContext;
  const options = uiSchema?.['ui:options'];

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const sortableItems = useMemo<Array<UniqueIdentifier>>(
    () => items.map((_, idx) => `${fieldPathId.$id}_${idx}`),
    [items, fieldPathId.$id],
  );

  const sortingStrategy = options?.layout === 'grid' ? rectSortingStrategy : verticalListSortingStrategy;

  const fieldDepth = useMemo(() => formIdToFieldDepthConverter(fieldPathId.$id), [fieldPathId.$id]);

  const handleDragSort = useCallback<ComponentProps<typeof DndContext>['onDragEnd']>(
    (event) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const baseId = `${fieldPathId.$id}_`;
      const activeIdStr = String(active.id);
      const overIdStr = String(over.id);

      if (!activeIdStr.startsWith(baseId) || !overIdStr.startsWith(baseId)) {
        return;
      }

      const activeIndex = parseInt(activeIdStr.slice(baseId.length), 10);
      const overIndex = parseInt(overIdStr.slice(baseId.length), 10);

      if (isNaN(activeIndex) || isNaN(overIndex) || activeIndex === overIndex) {
        return;
      }

      formContext?.reorderArrayItems(fieldDepth, activeIndex, overIndex);
    },
    [fieldPathId.$id, fieldDepth, formContext],
  );

  const gridTemplateColumns = useMemo<ComponentProps<typeof Box>['gridTemplateColumns']>(
    () => (options?.layout === 'grid' ? 'repeat(auto-fit, minmax(320px, 1fr))' : '1fr'),
    [options?.layout],
  );

  const { parentTitle, arrayName } = useMemo(() => {
    const [parentName, parentIndex, arrayName] = fieldDepth.slice(
      fieldDepth.length - 3, // the last 3 entries, if existing, represent the relevant identifiers
    );

    return {
      parentTitle: camelCaseToSpaceSeparated(parentName).toLowerCase(),
      parentName,
      parentIndex: Number(parentIndex),
      arrayName,
    };
  }, [fieldDepth]);

  const canCopyFromSibling = !!options?.canCopyFromSibling && canAdd;

  const canCopyArrayItems = !!options?.canCopyArrayItems && Array.isArray(formData) && formData.length > 1;

  const canCopyArrayItemsFromSimilar = options?.canCopyArrayItemsFromSimilar;

  const uncopyableProperties: Array<string> = useMemo(
    () => (Array.isArray(options?.uncopyableProperties) ? options?.uncopyableProperties : []),
    [options],
  );

  const fieldTitleProperty: string = options?.itemTitleProperty ? String(options.itemTitleProperty) : 'title';

  const handleSiblingParentArrayTrigger = useCallback(() => {
    if (!formContext) {
      return;
    }
    formContext.triggerSiblingParentArrayCopy({
      fieldDepth,
      uncopyableProperties,
      canCopyArrayItemsFromSimilar,
      fieldTitleProperty,
      properties: (schema.items as JSONSchema7)?.properties,
    });
  }, [formContext, fieldDepth, uncopyableProperties, fieldTitleProperty, canCopyArrayItemsFromSimilar, schema]);

  const handleItemCopyTrigger = useCallback(
    (currentItemIndex: number) => {
      if (!formContext) {
        return;
      }
      formContext.triggerArrayItemCopy({
        currentItemIndex,
        fieldDepth,
        uncopyableProperties,
        fieldTitleProperty,
      });
    },
    [fieldDepth, fieldTitleProperty, formContext, uncopyableProperties],
  );

  const [inclusionItemsGenerateButtonDisabled, setInclusionItemsGenerateButtonDisabled] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();
  const handleInclusionItemsGenerate = useCallback(async () => {
    const tourOptionIndex = parseInt(fieldPathId.$id.split('_')[2], 10);
    setInclusionItemsGenerateButtonDisabled(true);
    try {
      await formContext?.handleInclusionItemsGenerate?.(tourOptionIndex);
    } catch (error) {
      enqueueSnackbar('Unable to generate inclusion items', {
        variant: 'error',
      });
    } finally {
      setInclusionItemsGenerateButtonDisabled(false);
    }
  }, [enqueueSnackbar, fieldPathId.$id, formContext]);

  const [lastOpenedIndex, setLastOpenedIndex] = useState<number>(0);

  const handleAccordionClicked = useCallback(
    (index: number) => {
      if (options?.displaySingleOption) {
        setLastOpenedIndex(index);
      }
    },
    [options?.displaySingleOption],
  );

  // Default to expanded unless explicitly set to collapsed
  const shouldDefaultExpand = !options?.isInitiallyCollapsed;

  return (
    <Badge badgeContent={items.length} showZero color="primary">
      <Paper variant="outlined" sx={{ width: '100%' }} className={className}>
        <Accordion defaultExpanded={shouldDefaultExpand} disableGutters elevation={0}>
          {!!title && (
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
                {!options?.simpleLayout && (
                  <BreadcrumbsLegendTemplate
                    id={fieldPathId.$id}
                    title={title}
                    required={required}
                    schema={schema}
                    registry={registry}
                  />
                )}
                {options?.simpleLayout && <Typography variant="h5">{title}</Typography>}
                <DataArrayIcon color="disabled" />
              </Stack>
            </AccordionSummary>
          )}

          <AccordionDetails sx={{ px: 1 }}>
            <Box display="grid" gap={2} gridTemplateColumns={gridTemplateColumns} justifyContent="center">
              {arrayName === 'inclusionItems' && (
                <Button
                  type="button"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={inclusionItemsGenerateButtonDisabled}
                  onClick={handleInclusionItemsGenerate}
                >
                  Generate
                </Button>
              )}
              {items.length === 0 && (
                <Typography variant="overline" color="secondary" sx={{ textAlign: 'center' }}>
                  Empty
                </Typography>
              )}
              <ArrayFieldItemContext.Provider
                value={{
                  arrayTitle: title,
                  toolbarDirection: options?.toolbarDirection,
                  onItemCopyTrigger: canCopyArrayItems ? handleItemCopyTrigger : undefined,
                  canCopyArrayItems,
                  fieldPathId: fieldPathId.$id,
                }}
              >
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragSort}>
                  <SortableContext items={sortableItems} strategy={sortingStrategy}>
                    {options?.displaySingleOption && (
                      <ActiveAccordionContext.Provider
                        value={{
                          activeAccordionIndex: lastOpenedIndex,
                          setActiveAccordion: handleAccordionClicked,
                          displaySingleOption: options?.displaySingleOption,
                          targetedSection: fieldPathId.$id.replace(/([0-9]|_)/g, ''),
                        }}
                      >
                        {items}
                      </ActiveAccordionContext.Provider>
                    )}
                    {!options?.displaySingleOption && items}
                  </SortableContext>
                </DndContext>
              </ArrayFieldItemContext.Provider>
            </Box>
          </AccordionDetails>

          <AccordionActions>
            <Stack direction="row" justifyContent="end" spacing={1} sx={{ width: '100%' }}>
              {canAdd && !options?.simpleLayout && (
                <Button
                  type="button"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={disabled || readonly}
                  onClick={onAddClick}
                >
                  Add {title}
                </Button>
              )}
              {canAdd && options?.simpleLayout && (
                <IconButton disabled={disabled || readonly} color="primary" onClick={onAddClick} size="large">
                  <AddIcon />
                </IconButton>
              )}
              {canCopyFromSibling && (
                <Tooltip placement="top" title={`Copy and add from sibling ${parentTitle}`}>
                  <IconButton
                    type="button"
                    color="secondary"
                    size="large"
                    disabled={disabled || readonly}
                    onClick={handleSiblingParentArrayTrigger}
                  >
                    <CopyAllIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </AccordionActions>
        </Accordion>
      </Paper>
    </Badge>
  );
}
